/* ============================================================
   COLOUR NAMING
   Converts a hex value into a human-readable colour name.

   Matching happens in CIE Lab space rather than RGB, because RGB
   distance does not match how people actually perceive colour —
   two greens can be far apart numerically but look identical.

   The palette leans towards the vocabulary a jewellery shop
   actually uses (Rose Gold, Antique Gold, Champagne, Gunmetal…)
   so detected names read naturally in a WhatsApp order message.
   ============================================================ */

export const NAMED_COLORS = [
  /* ── Metallics — the ones that matter most for jewellery ── */
  { name: 'Gold', hex: '#D4AF37' },
  { name: 'Bright Gold', hex: '#F1C846' },
  { name: 'Antique Gold', hex: '#A67C2E' },
  { name: 'Champagne Gold', hex: '#E4CFA3' },
  { name: 'Rose Gold', hex: '#B76E79' },
  { name: 'Blush Rose Gold', hex: '#E0B0A8' },
  { name: 'Silver', hex: '#C0C0C0' },
  { name: 'Bright Silver', hex: '#E3E3E3' },
  { name: 'Oxidised Silver', hex: '#8C8C8C' },
  { name: 'Platinum', hex: '#D9D8D2' },
  { name: 'Gunmetal', hex: '#4A4E54' },
  { name: 'Bronze', hex: '#9C6B30' },
  { name: 'Copper', hex: '#B87333' },
  { name: 'Pearl White', hex: '#F4F0E6' },

  /* ── Neutrals ───────────────────────────────────────────── */
  { name: 'White', hex: '#FFFFFF' },
  { name: 'Ivory', hex: '#F6F1E7' },
  { name: 'Cream', hex: '#F3E9D6' },
  { name: 'Beige', hex: '#E4D5BE' },
  { name: 'Light Grey', hex: '#D3D3D3' },
  { name: 'Grey', hex: '#9E9E9E' },
  { name: 'Charcoal', hex: '#36393D' },
  { name: 'Matte Black', hex: '#111111' },
  { name: 'Jet Black', hex: '#050505' },

  /* ── Browns ─────────────────────────────────────────────── */
  { name: 'Tan', hex: '#C8956C' },
  { name: 'Camel', hex: '#B08A57' },
  { name: 'Chocolate Brown', hex: '#5A3A22' },
  { name: 'Coffee', hex: '#6F4E37' },
  { name: 'Taupe', hex: '#8B7D6B' },

  /* ── Reds & pinks ───────────────────────────────────────── */
  { name: 'Red', hex: '#D32F2F' },
  { name: 'Ruby Red', hex: '#9B111E' },
  { name: 'Maroon', hex: '#6E1423' },
  { name: 'Coral', hex: '#F0725C' },
  { name: 'Rose Pink', hex: '#E86A92' },
  { name: 'Hot Pink', hex: '#E91E8C' },
  { name: 'Baby Pink', hex: '#F5C6D6' },
  { name: 'Fuchsia', hex: '#C2185B' },
  { name: 'Peach', hex: '#F7B98C' },

  /* ── Oranges & yellows ──────────────────────────────────── */
  { name: 'Orange', hex: '#EF6C00' },
  { name: 'Amber', hex: '#F0A202' },
  { name: 'Mustard', hex: '#D4A017' },
  { name: 'Lemon Yellow', hex: '#F4E04D' },

  /* ── Greens ─────────────────────────────────────────────── */
  { name: 'Green', hex: '#2E9E52' },
  { name: 'Emerald Green', hex: '#0F7B4F' },
  { name: 'Bottle Green', hex: '#0B4A32' },
  { name: 'Olive', hex: '#7A7B32' },
  { name: 'Pista Green', hex: '#93C572' },
  { name: 'Mint Green', hex: '#B9E5CE' },
  { name: 'Teal', hex: '#00796B' },

  /* ── Blues ──────────────────────────────────────────────── */
  { name: 'Royal Blue', hex: '#2456D3' },
  { name: 'Navy Blue', hex: '#12224F' },
  { name: 'Sky Blue', hex: '#7EC8E3' },
  { name: 'Sapphire Blue', hex: '#0F52BA' },
  { name: 'Powder Blue', hex: '#C3D9E8' },
  { name: 'Turquoise', hex: '#30BFBF' },

  /* ── Purples ────────────────────────────────────────────── */
  { name: 'Purple', hex: '#7B3FA0' },
  { name: 'Deep Purple', hex: '#4A148C' },
  { name: 'Lavender', hex: '#C9B6E4' },
  { name: 'Plum', hex: '#8E4585' },
  { name: 'Mauve', hex: '#B287A3' },
];

/* ── Hex ⇄ RGB ─────────────────────────────────────────────── */

export function hexToRgb(hex) {
  const clean = String(hex || '').replace('#', '').trim();
  const full = clean.length === 3
    ? clean.split('').map((c) => c + c).join('')
    : clean.padEnd(6, '0').slice(0, 6);
  return {
    r: parseInt(full.slice(0, 2), 16) || 0,
    g: parseInt(full.slice(2, 4), 16) || 0,
    b: parseInt(full.slice(4, 6), 16) || 0,
  };
}

export function rgbToHex(r, g, b) {
  const to = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return ('#' + to(r) + to(g) + to(b)).toUpperCase();
}

/* ── sRGB → CIE Lab ────────────────────────────────────────── */

function srgbToLinear(c) {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

export function rgbToLab(r, g, b) {
  const lr = srgbToLinear(r);
  const lg = srgbToLinear(g);
  const lb = srgbToLinear(b);

  // sRGB D65 → XYZ
  let x = (lr * 0.4124564 + lg * 0.3575761 + lb * 0.1804375) / 0.95047;
  let y = (lr * 0.2126729 + lg * 0.7151522 + lb * 0.0721750) / 1.0;
  let z = (lr * 0.0193339 + lg * 0.1191920 + lb * 0.9503041) / 1.08883;

  const f = (t) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
  x = f(x); y = f(y); z = f(z);

  return {
    L: 116 * y - 16,
    a: 500 * (x - y),
    b: 200 * (y - z),
  };
}

/* Perceptual distance (CIE76 — accurate enough for naming, and
   far cheaper than CIEDE2000 when run over thousands of pixels). */
export function deltaE(lab1, lab2) {
  const dL = lab1.L - lab2.L;
  const da = lab1.a - lab2.a;
  const db = lab1.b - lab2.b;
  return Math.sqrt(dL * dL + da * da + db * db);
}

export function hexDistance(hexA, hexB) {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  return deltaE(rgbToLab(a.r, a.g, a.b), rgbToLab(b.r, b.g, b.b));
}

/* Pre-compute Lab for the palette once, at module load. */
const PALETTE_LAB = NAMED_COLORS.map((c) => {
  const { r, g, b } = hexToRgb(c.hex);
  return { ...c, lab: rgbToLab(r, g, b), hsl: rgbToHsl(r, g, b) };
});

/* ── HSL helpers (used to sharpen a few naming edge cases) ─── */

export function rgbToHsl(r, g, b) {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0));
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    h *= 60;
  }
  return { h, s, l };
}

/**
 * Best human name for a hex colour.
 * @returns {{ name: string, hex: string, match: string, distance: number }}
 */
export function nameForHex(hex) {
  const { r, g, b } = hexToRgb(hex);
  const lab = rgbToLab(r, g, b);
  const { s, l } = rgbToHsl(r, g, b);

  let best = PALETTE_LAB[0];
  let bestD = Infinity;

  for (const c of PALETTE_LAB) {
    let d = deltaE(lab, c.lab);

    // A near-neutral pixel should not be named after a saturated
    // colour just because it is numerically close, and vice versa.
    d += Math.abs(s - c.hsl.s) * 18;

    if (d < bestD) { bestD = d; best = c; }
  }

  // Very dark or very light results read better with plain names.
  if (l < 0.06) best = { name: 'Jet Black', hex: '#050505' };
  else if (l > 0.965 && s < 0.08) best = { name: 'White', hex: '#FFFFFF' };

  return {
    name: best.name,
    hex: String(hex).toUpperCase(),
    match: best.hex,
    distance: Math.round(bestD * 10) / 10,
  };
}

/** Readable text colour (black or white) for a given background. */
export function contrastText(hex) {
  const { r, g, b } = hexToRgb(hex);
  // Relative luminance, WCAG formula. 0.179 is the crossover point at
  // which black text stops out-contrasting white text — using a higher
  // threshold puts white text on mid-tones like gold, where black wins
  // by a wide margin.
  const lum = 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
  return lum > 0.179 ? '#1A1A1A' : '#FFFFFF';
}
